'use client';

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { SelectProps, SelectSize } from './types';
import { twMerge } from 'tailwind-merge';

export type SelectVariant = 'outlined' | 'soft' | 'plain' | 'rounded';
// Add export keyword here
export type SelectColor =
  | 'primary'
  | 'neutral'
  | 'danger'
  | 'success'
  | 'warning';

// Simple throttle function
function throttle<T extends (...args: any[]) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  let lastFunc: NodeJS.Timeout;
  let lastRan: number;
  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      lastRan = Date.now();
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= limit) {
          func.apply(this, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
}

const getContainerClasses = (
  variant: SelectVariant,
  size: SelectSize,
  error: boolean,
  disabled: boolean | undefined,
  focused: boolean,
  color: SelectColor
) => {
  const baseClasses =
    'relative flex items-center w-full rounded-lg cursor-pointer transition-all duration-200';

  const effectiveColor = error ? 'danger' : color;

  // Default focus styles are defined here based on the 'focused' state
  const colorClasses = {
    primary: {
      outlined: [
        'border border-neutral-200',
        'hover:border-primary-500',
        disabled ? 'border-neutral-100' : '',
      ].join(' '),
      soft: [
        'bg-primary-50',
        'hover:bg-primary-100/50',
        disabled ? 'bg-neutral-50' : '',
      ].join(' '),
      plain: ['hover:bg-primary-50/50', disabled ? 'bg-transparent' : ''].join(
        ' '
      ),
    },
    neutral: {
      outlined: [
        'border border-neutral-200 bg-white',
        focused // Default focus style for outlined neutral
          ? 'border-neutral-500 ring-1 ring-neutral-500/20'
          : 'hover:border-neutral-500',
        disabled ? 'border-neutral-100 bg-neutral-50' : '',
      ].join(' '),
      soft: [
        'bg-neutral-100/50',
        'hover:bg-neutral-100',
        disabled ? 'bg-neutral-50' : '',
      ].join(' '),
      plain: ['hover:bg-neutral-50', disabled ? 'bg-transparent' : ''].join(
        ' '
      ),
    },
    danger: {
      outlined: [
        'border',
        error ? 'border-red-500' : 'border-neutral-200',
        focused ? 'ring-1 ring-red-500/20' : 'hover:border-red-500',
        disabled ? 'border-neutral-100' : '',
      ].join(' '),
      soft: [
        'bg-red-50',
        'hover:bg-red-100/50',
        disabled ? 'bg-neutral-50' : '',
      ].join(' '),
      plain: ['hover:bg-red-50/50', disabled ? 'bg-transparent' : ''].join(' '),
    },
    success: {
      outlined: [
        'border border-neutral-200',
        'hover:border-green-500',
        disabled ? 'border-neutral-100' : '',
      ].join(' '),
      soft: [
        'bg-green-50',
        'hover:bg-green-100/50',
        disabled ? 'bg-neutral-50' : '',
      ].join(' '),
      plain: ['hover:bg-green-50/50', disabled ? 'bg-transparent' : ''].join(
        ' '
      ),
    },
    warning: {
      outlined: [
        'border border-neutral-200',
        'hover:border-yellow-500',
        disabled ? 'border-neutral-100' : '',
      ].join(' '),
      soft: [
        'bg-yellow-50',
        'hover:bg-yellow-100/50',
        disabled ? 'bg-neutral-50' : '',
      ].join(' '),
      plain: ['hover:bg-yellow-50/50', disabled ? 'bg-transparent' : ''].join(
        ' '
      ),
    },
  };

  const variantClasses = colorClasses[effectiveColor][variant];

  const sizeClasses = {
    sm: 'min-h-8 text-xs',
    md: 'min-h-10 text-sm',
    lg: 'min-h-12 text-sm',
  };

  return twMerge(baseClasses, variantClasses, sizeClasses[size]);
};

const getPaddingClasses = (size: SelectSize, hasStartDecorator: boolean) => {
  const basePadding = {
    sm: 'py-1.5',
    md: 'py-2',
    lg: 'py-3',
  };

  const startPadding = {
    sm: hasStartDecorator ? 'pl-2' : 'pl-3',
    md: hasStartDecorator ? 'pl-2' : 'pl-3',
    lg: hasStartDecorator ? 'pl-3' : 'pl-4',
  };

  const endPadding = {
    sm: 'pr-2',
    md: 'pr-2',
    lg: 'pr-3',
  };

  return twMerge(basePadding[size], startPadding[size], endPadding[size]);
};

const getDecoratorClasses = (
  size: SelectSize,
  disabled: boolean | undefined,
  position: 'start' | 'end'
) => {
  const baseClasses = 'flex items-center';
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };
  const paddingClasses = {
    start: {
      sm: 'pl-3',
      md: 'pl-3',
      lg: 'pl-4',
    },
    end: {
      sm: 'pr-3',
      md: 'pr-3',
      lg: 'pr-4',
    },
  };
  const colorClasses = disabled ? 'text-neutral-300' : 'text-neutral-500';

  return twMerge(
    baseClasses,
    sizeClasses[size],
    paddingClasses[position][size],
    colorClasses
  );
};

const getOptionClasses = (
  size: SelectSize,
  disabled: boolean | undefined,
  isSelected: boolean,
  className?: string
) => {
  const baseClasses =
    'flex items-center gap-2 cursor-pointer text-neutral-900 transition-colors duration-200';
  const sizeClasses = {
    sm: 'py-1.5 px-3 text-sm',
    md: 'py-2 px-3 text-sm',
    lg: 'py-3 px-4 text-sm',
  };
  const stateClasses = [
    'hover:bg-neutral-100/50 active:bg-neutral-100',
    disabled ? 'opacity-50 cursor-not-allowed' : '',
    isSelected ? 'bg-neutral-50' : '',
  ].join(' ');

  return twMerge(baseClasses, sizeClasses[size], stateClasses, className);
};


const Select: React.FC<SelectProps> = ({
  id,
  value,
  defaultValue,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  error = false,
  required = false,
  label,
  helperText,
  className = '',
  children,
  size = 'sm',
  variant = 'outlined',
  color = 'neutral',
  'aria-label': ariaLabel,
  startDecorator,
  endDecorator = <Icon icon={faCaretDown} className="w-3.5 h-3.5" />,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<
    string | number | null | undefined
  >(value ?? defaultValue);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const [focused, setFocused] = useState(false);
  const [positioningComplete, setPositioningComplete] = useState(false);

  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);

  const updateDropdownPosition = useCallback(() => {
    if (triggerRef.current && dropdownRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const actualDropdownHeight = dropdownRef.current.offsetHeight;
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - rect.bottom;
      const dropdownMargin = 4;

      const shouldOpenUpward = spaceBelow < actualDropdownHeight;

      let newTop;
      if (shouldOpenUpward) {
        newTop = rect.top - actualDropdownHeight - dropdownMargin;
      } else {
        newTop = rect.bottom + dropdownMargin;
      }

      setPosition({
        top: newTop,
        left: rect.left,
        width: rect.width,
      });

      setPositioningComplete(true);
    }
  }, []);

  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  useEffect(() => {
    const throttledUpdate = throttle(updateDropdownPosition, 100);

    if (isOpen) {
      updateDropdownPosition();

      window.addEventListener('resize', throttledUpdate);
      document.addEventListener('scroll', throttledUpdate, true);

      return () => {
        window.removeEventListener('resize', throttledUpdate);
        document.removeEventListener('scroll', throttledUpdate, true);
      };
    } else {
      window.removeEventListener('resize', throttledUpdate);
      document.removeEventListener('scroll', throttledUpdate, true);
    }
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setFocused(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setPositioningComplete(false);
    }
  }, [isOpen]);

  const handleSelect = (event: React.MouseEvent, newValue: string | number) => {
    onChange?.(event, newValue);
    setSelectedValue(newValue);
    setIsOpen(false);
    setFocused(false);
  };

  const selectedOption = React.Children.toArray(children).find(
    (child) =>
      React.isValidElement(child) &&
      String((child.props as Record<string, any>).value) === String(selectedValue)
  ) as React.ReactElement;

  const handleOptionClick = (
    event: React.MouseEvent,
    optionValue: string | number
  ) => {
    event.stopPropagation();
    if (!disabled) {
      handleSelect(event, optionValue);
    }
  };

  const options = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return null;

    const childProps = child.props as Record<string, any>;
    const {
      value: optionValue,
      disabled: optionDisabled,
      className: optionClassName,
    } = childProps;
    const isSelected = String(selectedValue) === String(optionValue);

    return (
      <li
        key={optionValue}
        role="option"
        aria-selected={isSelected}
        data-value={optionValue}
        onClick={(event: React.MouseEvent) =>
          handleOptionClick(event, optionValue)
        }
        className={getOptionClasses(
          size,
          optionDisabled,
          isSelected,
          optionClassName
        )}
        data-disabled={optionDisabled}
      >
        {childProps.startDecorator && (
          <span
            className={getDecoratorClasses(
              size,
              optionDisabled ?? disabled,
              'start'
            )}
          >
            {childProps.startDecorator}
          </span>
        )}
        <span className="flex-grow min-w-0">{childProps.children}</span>
        {childProps.endDecorator && (
          <span
            className={getDecoratorClasses(
              size,
              optionDisabled ?? disabled,
              'end'
            )}
          >
            {childProps.endDecorator}
          </span>
        )}
      </li>
    );
  });

  return (
    <div className={twMerge('relative z-0', className)}>
      {label && (
        <label
          htmlFor={id}
          className={twMerge(
            'block mb-1 text-sm font-medium',
            error ? 'text-red-500' : 'text-neutral-800'
          )}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div
        ref={triggerRef}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={isOpen ? `${id}-listbox` : undefined}
        aria-label={ariaLabel}
        tabIndex={disabled ? -1 : 0}
        className={twMerge(
          getContainerClasses(variant, size, error, disabled, focused, color),
          className
        )}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onFocus={() => !disabled && setFocused(true)}
        onBlur={() => !isOpen && setFocused(false)}
        onKeyDown={(e) => {
          if (!disabled) {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsOpen(!isOpen);
            } else if (e.key === 'Escape' && isOpen) {
              e.preventDefault();
              setIsOpen(false);
              setFocused(false);
              triggerRef.current?.blur();
            }
          }
        }}
      >
        {startDecorator && (
          <span className={getDecoratorClasses(size, disabled, 'start')}>
            {startDecorator}
          </span>
        )}
        <span
          className={twMerge(
            'flex-1 min-w-0 flex items-center',
            getPaddingClasses(size, !!startDecorator),
            !selectedValue && 'text-neutral-500',
            disabled
              ? 'cursor-not-allowed text-neutral-300'
              : 'text-neutral-900'
          )}
        >
          {selectedOption ? (selectedOption.props as Record<string, any>).children : placeholder}
        </span>
        {endDecorator && (
          <span className={getDecoratorClasses(size, disabled, 'end')}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                transformBox: 'fill-box',
                transformOrigin: 'center center',
                transition: 'transform 200ms ease-out',
                transform: isOpen ? 'rotate(-90deg)' : 'rotate(0deg)',
              }}
            >
              {endDecorator}
            </span>
          </span>
        )}
      </div>

      {helperText && (
        <p
          className={twMerge(
            'mt-1 text-sm',
            error ? 'text-red-500' : 'text-neutral-600'
          )}
        >
          {helperText}
        </p>
      )}

      {isOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999]" data-select-portal="true">
            <ul
              ref={dropdownRef}
              role="listbox"
              id={`${id}-listbox`}
              className={twMerge(
                'absolute py-1 mt-1 bg-white border border-neutral-200',
                'rounded-lg shadow-lg max-h-60 overflow-y-auto focus:outline-none',
                !positioningComplete && 'invisible'
              )}
              style={{
                top: position.top,
                left: position.left,
                width: position.width,
              }}
            >
              {options}
            </ul>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Select;
